import { CartAddEvent } from '@theme/events';

class JewelleryChocolateBuilder extends HTMLElement {
  #baseInputs;
  #jewelleryInputs;
  #submitButton;
  #status;
  #baseSummary;
  #baseSummaryList;
  #jewellerySummary;
  #jewellerySummaryList;
  #totalSummary;
  #selectionChanged = false;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.#submitButton = this.querySelector('[data-builder-submit]');
    this.#status = this.querySelector('[data-builder-status]');
    this.#baseSummary = this.querySelector('[data-summary-base]');
    this.#baseSummaryList = this.querySelector('[data-summary-base-list]');
    this.#jewellerySummary = this.querySelector('[data-summary-jewellery]');
    this.#jewellerySummaryList = this.querySelector('[data-summary-jewellery-list]');
    this.#totalSummary = this.querySelector('[data-summary-total]');

    this.addEventListener('change', this.#handleSelection);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleTabKeydown);
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);
    this.#updateSummary();
    void this.#restoreCartSelection();
  }

  #handleSelection = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#selectionChanged = true;

    if (input.name === 'chocolate-base') this.#baseInputs = this.#getBaseInputs();
    if (input.name === 'jewellery-choice') {
      if (input.checked) {
        this.querySelectorAll('input[name="jewellery-choice"]').forEach((choice) => {
          if (choice !== input && choice.value === input.value) choice.checked = false;
        });
      }
      this.#jewelleryInputs = this.#getJewelleryInputs();
    }

    this.#updateSummary();

    const nextTarget = input.name === 'chocolate-base' ? this.querySelector('#jewellery-options') : null;
    if (nextTarget && window.matchMedia('(max-width: 749px)').matches) {
      nextTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  async #restoreCartSelection() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (this.#selectionChanged || !Array.isArray(cart.items)) return;

      const customizerItems = cart.items.filter((item) => {
        const properties = item.properties || {};
        return properties._customization_id && properties._customization_role;
      });
      if (customizerItems.length === 0) return;

      const baseVariantIds = new Set(
        customizerItems
          .filter((item) => item.properties._customization_role === 'base')
          .map((item) => String(item.variant_id)),
      );
      const jewelleryVariantIds = new Set(
        customizerItems
          .filter((item) => item.properties._customization_role === 'component')
          .map((item) => String(item.variant_id)),
      );

      if (this.#selectionChanged) return;

      this.querySelectorAll('input[name="chocolate-base"]').forEach((input) => {
        input.checked = baseVariantIds.has(input.value);
      });
      this.querySelectorAll('input[name="jewellery-choice"]').forEach((input) => {
        input.checked = jewelleryVariantIds.has(input.value);
      });
      this.#updateSummary();
    } catch {
      return;
    }
  }

  #handleClick = (event) => {
    const tab = event.target.closest('[data-jewellery-tab]');
    if (!tab) return;

    const category = tab.dataset.jewelleryTab;
    this.querySelectorAll('[data-jewellery-tab]').forEach((button) => {
      const active = button === tab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });

    this.querySelectorAll('[data-jewellery-panel]').forEach((panel) => {
      const active = panel.dataset.jewelleryPanel === category;
      panel.hidden = !active;
    });

    if (window.matchMedia('(max-width: 749px)').matches) {
      tab.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }
  };

  #handleTabKeydown = (event) => {
    const tab = event.target.closest('[data-jewellery-tab]');
    if (!tab) return;

    const tabs = Array.from(this.querySelectorAll('[data-jewellery-tab]'));
    const currentIndex = tabs.indexOf(tab);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    tabs[nextIndex].focus();
    tabs[nextIndex].click();
  };

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (this.dataset.previewOnly === 'true') return;

    const baseInputs = this.#getBaseInputs();
    const jewelleryInputs = this.#getJewelleryInputs();
    if (baseInputs.length === 0 || jewelleryInputs.length === 0 || !this.#submitButton) return;

    this.#baseInputs = baseInputs;
    this.#jewelleryInputs = jewelleryInputs;

    this.#submitButton.disabled = true;
    this.#submitButton.dataset.loading = 'true';
    this.#setStatus('Adding your custom gift to the bag…', 'loading');

    const customizationId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `custom-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const baseTitles = baseInputs.map((input) => input.dataset.title || 'Chocolate');
    const jewelleryTitles = jewelleryInputs.map((input) => input.dataset.title || 'Jewellery');
    const baseVariantIds = baseInputs.map((input) => input.value).join(',');
    const jewelleryVariantIds = jewelleryInputs.map((input) => input.value).join(',');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const items = baseInputs.map((input) => ({
      id: Number(input.value),
      quantity: 1,
      properties: {
        'Chocolate choices': baseTitles.join(', '),
        'Jewellery choices': jewelleryTitles.join(', '),
        _customization_id: customizationId,
        _customization_role: 'base',
        _paired_variant_ids: jewelleryVariantIds,
      },
    }));

    jewelleryInputs.forEach((input, index) => {
      items.push({
        id: Number(input.value),
        quantity: 1,
        properties: {
          'Chocolate boxes': baseTitles.join(', '),
          'Customization piece': `${index + 1} of ${jewelleryInputs.length}`,
          _customization_id: customizationId,
          _customization_role: 'component',
          _paired_variant_ids: baseVariantIds,
        },
      });
    });

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });

      const responseData = await response.json();
      if (!response.ok || responseData.status) {
        throw new Error(responseData.description || responseData.message || 'This selection could not be added.');
      }

      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'jewellery-chocolate-builder',
          itemCount: cart.item_count,
          sections: responseData.sections,
        })
      );

      this.#setStatus('Taking you to checkout…', 'loading');
      window.location.assign(`${window.Shopify.routes.root}checkout`);
    } catch (error) {
      this.#setStatus(error instanceof Error ? error.message : 'Something went wrong. Please try again.', 'error');
      this.#submitButton.disabled = false;
    } finally {
      delete this.#submitButton.dataset.loading;
    }
  };

  #getBaseInputs() {
    const seenVariantIds = new Set();

    return Array.from(this.querySelectorAll('input[name="chocolate-base"]:checked')).filter((input) => {
      if (seenVariantIds.has(input.value)) return false;
      seenVariantIds.add(input.value);
      return true;
    });
  }

  #getJewelleryInputs() {
    const seenVariantIds = new Set();

    return Array.from(this.querySelectorAll('input[name="jewellery-choice"]:checked')).filter((input) => {
      if (seenVariantIds.has(input.value)) return false;
      seenVariantIds.add(input.value);
      return true;
    });
  }

  #updateSummary() {
    this.#baseInputs = this.#getBaseInputs();
    this.#jewelleryInputs = this.#getJewelleryInputs();

    const basePrice = this.#baseInputs.reduce(
      (total, input) => total + Number(input.dataset.price || 0),
      0,
    );
    const jewelleryPrice = this.#jewelleryInputs.reduce(
      (total, input) => total + Number(input.dataset.price || 0),
      0,
    );
    const baseTitles = this.#baseInputs.map((input) => input.dataset.title || 'Chocolate');
    const jewelleryTitles = this.#jewelleryInputs.map((input) => input.dataset.title || 'Jewellery');

    if (this.#baseSummary) {
      this.#baseSummary.querySelector('[data-summary-name]').textContent = this.#baseInputs.length
        ? `${this.#baseInputs.length} chocolate${this.#baseInputs.length === 1 ? '' : 's'}`
        : 'Choose chocolates';
      this.#baseSummary.querySelector('[data-summary-price]').textContent = this.#baseInputs.length
        ? this.#formatMoney(basePrice)
        : '—';
    }

    if (this.#baseSummaryList) {
      this.#baseSummaryList.textContent = baseTitles.join(' · ');
      this.#baseSummaryList.hidden = this.#baseInputs.length === 0;
    }

    if (this.#jewellerySummary) {
      this.#jewellerySummary.querySelector('[data-summary-name]').textContent = this.#jewelleryInputs.length
        ? `${this.#jewelleryInputs.length} jewellery piece${this.#jewelleryInputs.length === 1 ? '' : 's'}`
        : 'Choose jewellery pieces';
      this.#jewellerySummary.querySelector('[data-summary-price]').textContent = this.#jewelleryInputs.length
        ? this.#formatMoney(jewelleryPrice)
        : '—';
    }

    if (this.#jewellerySummaryList) {
      this.#jewellerySummaryList.textContent = jewelleryTitles.join(' · ');
      this.#jewellerySummaryList.hidden = this.#jewelleryInputs.length === 0;
    }

    if (this.#totalSummary) this.#totalSummary.textContent = this.#formatMoney(basePrice + jewelleryPrice);

    if (this.#submitButton) {
      const hasCompleteSelection = this.#baseInputs.length > 0 && this.#jewelleryInputs.length > 0;
      this.#submitButton.disabled = this.dataset.previewOnly === 'true' || !hasCompleteSelection;
      if (hasCompleteSelection) delete this.#submitButton.dataset.added;
    }

    if (this.#status) this.#setStatus('', 'idle');
  }

  #formatMoney(cents) {
    const currency = this.dataset.currency || 'INR';
    const locale = document.documentElement.lang || 'en-IN';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  #setStatus(message, state) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.dataset.state = state;
  }
}

if (!customElements.get('jewellery-chocolate-builder')) {
  customElements.define('jewellery-chocolate-builder', JewelleryChocolateBuilder);
}
